import { Button } from "@/components/ui/baseUi/button"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { useProgramBundle, useProgramBundleDelete } from "@modules/programBundle/hooks/useProgramBundle"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import { getDisplayVisibility, getSubscriptionCountText } from "@modules/programs/utils/programUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { EditIcon } from "@modules/ui/icons/editIcon"
import { BoxesIcon, DotIcon, MessageCircleMore, Share } from "lucide-react"
import { useEffect, useMemo } from "react"

type ProgramBundleInfoHeaderProps = {
  programBundleId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramBundleInfoHeader({ programBundleId, className, ...props }: ProgramBundleInfoHeaderProps) {
  const { data: programBundle } = useProgramBundle(programBundleId)
  const { sidebarNavigateBack, setCustomHeaderButtons } = useSidebar()
  const { isAuthor } = useProgramBundleRights(programBundleId)
  const { navigateToProgramBundleForm } = useProgramBundleSidebar()
  const navigate = useMySpaceNavigate()
  const { deleteProgramBundle, isPending } = useProgramBundleDelete()
  const { programBundleStripeSubscriptionInfos } = useProgramBundleAthleteSubscription(programBundle)
  const confirmPopup = useConfirmPopup()

  const canEditProgramBundle = useMemo(() => {
    return isAuthor
  }, [isAuthor])

  const canDeleteProgramBundle = useMemo(() => {
    return programBundle.type !== ProgramBundleType.ON_OFF && !isPending && isAuthor
  }, [programBundle.type, isPending, isAuthor])

  const canUserShareInvitationLink = useMemo(
    () =>
      programBundle &&
      programBundle.invitationLink &&
      (programBundle.visibility === ProgramVisibility.PUBLIC || isAuthor),
    [programBundle, isAuthor],
  )

  useEffect(() => {
    const customHeaderIcons = []

    const canUserChatWithMembers =
      (isAuthor || programBundleStripeSubscriptionInfos?.canSubscriberAccessToChat()) &&
      programBundle.type !== ProgramBundleType.ON_OFF

    if (canUserChatWithMembers) {
      const openChatWithProgramGroup = () => {
        if (canUserChatWithMembers) {
          if (programBundle.id) {
            navigate(Path.Chat.Root, { state: { programToChat: programBundle.id } })
          }
        }
      }

      customHeaderIcons.push(
        <Tooltip>
          <TooltipTrigger
            render={
              <Button variant="outline" size="iconMd" onClick={openChatWithProgramGroup}>
                <MessageCircleMore color="currentColor" size={16} />
              </Button>
            }
          />
          <TooltipContent side="bottom">
            <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
              {word("chat.button.goToProgramChat")}
            </Text>
          </TooltipContent>
        </Tooltip>,
      )
    }

    if (customHeaderIcons.length > 0) {
      setCustomHeaderButtons(customHeaderIcons)
    }
  }, [isAuthor, programBundle.invitationLink, programBundle.visibility])

  return (
    <div className={cn("relative flex h-60 w-full overflow-hidden rounded-b-4xl", className)} {...props}>
      {/* LFET SIDE TEXT */}
      <div className="mx-2 mb-7 flex w-5/10 flex-col items-center justify-center pt-12">
        <Text fontStyle="HS" className="line-clamp-2 text-center text-textDefault-heading">
          {programBundle.name}
        </Text>

        <div className="flex w-full items-center justify-center gap-1">
          <BoxesIcon size={12} />
          <Text fontStyle="BS" className="text-textDefault-heading">
            {`${word("programBundle.info.labelShort")} x${programBundle.nbPrograms} (${getDisplayVisibility(programBundle.visibility)})`}
          </Text>
          <DotIcon width={14} />
          <Text fontStyle="BS" className="text-textDefault-heading">
            {`${getSubscriptionCountText(
              programBundle.subscriptionCount,
              isAuthor,
              false,
              true,
              programBundle.type === ProgramBundleType.ON_OFF ? ProgramType.ONOFF : ProgramType.VIRTUAL,
            ).toLowerCase()}`}
          </Text>
        </div>
      </div>

      {/* RIGHT SIDE IMAGE */}
      {programBundle.image ? (
        <div
          className="h-full w-5/10"
          style={{ borderBottomRightRadius: "1rem", clipPath: "polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%)" }}
        >
          <img src={programBundle.image?.url} alt="programBundle image" className="h-full w-full object-cover" />
        </div>
      ) : null}

      {(canEditProgramBundle || canDeleteProgramBundle) && (
        <div className="absolute top-15 right-4">
          <EllipsisMenu propsContent={{ side: "bottom" }}>
            {canUserShareInvitationLink && (
              <DropdownMenuItem
                key="share"
                onClick={() => {
                  if (programBundle.invitationLink) {
                    navigator.clipboard.writeText(programBundle.invitationLink)
                    toast.success(word("program.details.share.success"), { classNames: successToastClassName() })
                  }
                }}
              >
                <Share color="currentColor" width={20} height={20} className="text-textDefault-body" />
                {word("sidebar.button.dynamicLink")}
              </DropdownMenuItem>
            )}

            {canEditProgramBundle && (
              <DropdownMenuItem
                key="pause"
                onClick={() => {
                  navigateToProgramBundleForm({
                    programBundleFormProps: { programBundle },
                  })
                }}
              >
                <EditIcon color="currentColor" width={20} height={20} className="text-textDefault-body" />
                {word("global.edit")}
              </DropdownMenuItem>
            )}

            {canDeleteProgramBundle && (
              <DropdownMenuItem
                key="delete"
                onClick={() => {
                  confirmPopup.show({
                    title: word("global.delete"),
                    message: word("programBundle.modale.delete.message", {
                      programName: programBundle.name,
                    }),
                    accept: () => {
                      return Promise.resolve(
                        void (() => {
                          deleteProgramBundle(programBundleId)
                            .then(() => {
                              toast.success(word("programBundle.info.delete.success"), {
                                classNames: successToastClassName(),
                              })
                              sidebarNavigateBack()
                            })
                            .catch(() => {
                              toast.error(word("programBundle.info.delete.error"), {
                                classNames: errorToastClassName(),
                              })
                            })
                        }),
                      )
                    },
                  })
                }}
              >
                <TrashIcon color="currentColor" width={20} height={20} className="text-textError" />
                <span className="text-textError">{word("global.delete")}</span>
              </DropdownMenuItem>
            )}
          </EllipsisMenu>
        </div>
      )}
    </div>
  )
}
